/**
 * Utility functions for chunking document content
 */
import { encode } from 'gpt-tokenizer';

/**
 * Count tokens in a text string
 * @param {string} text - The text to count tokens for
 * @returns {number} - The number of tokens
 */
export const countTokens = (text) => {
  if (!text) return 0;
  return encode(text).length;
};

/**
 * Chunk a large text into smaller pieces based on token limit
 * @param {string} text - The text to chunk
 * @param {number} maxTokens - Maximum tokens per chunk
 * @returns {string[]} - Array of text chunks
 */
export const chunkText = (text, maxTokens) => {
  if (!text) return [];
  
  // If the text is already under the token limit, return it as is
  if (countTokens(text) <= maxTokens) {
    return [text];
  }
  
  // Split text into paragraphs or sentences
  const paragraphs = text.split(/\n\s*\n/); // Split by paragraph breaks
  const chunks = [];
  let currentChunk = "";
  let currentChunkTokens = 0;
  
  // Process paragraph by paragraph
  for (const paragraph of paragraphs) {
    const paragraphTokens = countTokens(paragraph);
    
    // If a single paragraph is too large, split by sentences
    if (paragraphTokens > maxTokens) {
      const sentences = paragraph.split(/(?<=[.!?])\s+/);
      
      for (const sentence of sentences) {
        const sentenceTokens = countTokens(sentence);
        
        // Handle sentences that are still too large (rare case)
        if (sentenceTokens > maxTokens) {
          // If current chunk has content, add it to chunks
          if (currentChunkTokens > 0) {
            chunks.push(currentChunk);
            currentChunk = "";
            currentChunkTokens = 0;
          }
          
          // Split the large sentence into smaller pieces
          let remainingSentence = sentence;
          while (remainingSentence) {
            const tokensThatFit = Math.floor(maxTokens * 0.9); // Leave some buffer
            const roughCharCountEstimate = Math.floor(tokensThatFit * 4); // ~4 chars per token
            
            const sentencePiece = remainingSentence.slice(0, roughCharCountEstimate);
            chunks.push(sentencePiece);
            
            remainingSentence = remainingSentence.slice(roughCharCountEstimate);
          }
        } 
        // Normal sentence processing
        else if (currentChunkTokens + sentenceTokens + 1 <= maxTokens) { // +1 for space
          if (currentChunkTokens > 0) {
            currentChunk += " ";
            currentChunkTokens += 1;
          }
          currentChunk += sentence;
          currentChunkTokens += sentenceTokens;
        } else {
          chunks.push(currentChunk);
          currentChunk = sentence;
          currentChunkTokens = sentenceTokens;
        }
      }
    } 
    // If paragraph fits in the current chunk
    else if (currentChunkTokens + paragraphTokens + 2 <= maxTokens) { // +2 for paragraph break
      if (currentChunkTokens > 0) {
        currentChunk += "\n\n";
        currentChunkTokens += 2;
      }
      currentChunk += paragraph;
      currentChunkTokens += paragraphTokens;
    } 
    // Start a new chunk with this paragraph
    else {
      if (currentChunkTokens > 0) {
        chunks.push(currentChunk);
      }
      currentChunk = paragraph;
      currentChunkTokens = paragraphTokens;
    }
  }
  
  // Add the last chunk if there's content
  if (currentChunkTokens > 0) {
    chunks.push(currentChunk);
  }
  
  return chunks;
};

/**
 * Prepare documents for AI processing with chunking if needed
 * @param {Array} documents - The documents to process
 * @param {number} maxContextTokens - Maximum tokens for context
 * @param {number} systemPromptTokens - Approximate tokens for system prompt
 * @param {number} userMessageTokens - Approximate tokens for user message
 * @returns {string} - Processed document content
 */
export const prepareDocumentsForAI = (documents, maxContextTokens, systemPromptTokens = 500, userMessageTokens = 50) => {
  if (!documents || documents.length === 0) return "";
  
  try {
    // Calculate available tokens for documents
    const availableTokensForDocs = maxContextTokens - systemPromptTokens - userMessageTokens;
    
    // If not enough tokens left for documents, return empty string
    if (availableTokensForDocs <= 0) return "";
    
    // Clean documents before processing
    const cleanedDocs = documents.map(doc => ({
      ...doc,
      name: sanitizeString(doc.name),
      content: sanitizeString(doc.content || '')
    }));
    
    // Combine all document content
    let allDocsContent = cleanedDocs.map(doc => 
      `Document: ${doc.name}\nContent: ${doc.content}`
    ).join('\n\n');
    
    // Check if combined content exceeds token limit
    const totalDocTokens = countTokens(allDocsContent);
    
    if (totalDocTokens <= availableTokensForDocs) {
      // If under limit, return as is
      return allDocsContent;
    } else {
      // Need to truncate/chunk content to fit token limit
      console.log('Chunking documents to fit token limit:', 
                  `${totalDocTokens} tokens > ${availableTokensForDocs} available`);
      
      // Create a simplified version with just document names and abbreviated content
      const simplifiedContent = cleanedDocs.map(doc => {
        // Allocate tokens proportionally to document size
        const docTokens = countTokens(doc.content);
        const docRatio = docTokens / totalDocTokens;
        const docAvailableTokens = Math.floor(availableTokensForDocs * docRatio * 0.9); // 0.9 for safety margin
        
        // Don't go below a minimum token count per document
        const minTokensPerDoc = Math.min(100, availableTokensForDocs / cleanedDocs.length);
        const tokensForThisDoc = Math.max(docAvailableTokens, minTokensPerDoc);
        
        // Chunk the document content and take only what fits
        const chunks = chunkText(doc.content, tokensForThisDoc);
        const truncatedContent = chunks[0] || ''; // Just take the first chunk
        
        return `Document: ${doc.name}\nContent: ${truncatedContent}\n[Content truncated due to length]`;
      }).join('\n\n');
      
      return simplifiedContent;
    }
  } catch (error) {
    console.error('Error preparing documents for AI:', error);
    return documents.map(doc => `Document: ${doc.name || 'Unknown'} (Error processing content)`).join('\n');
  }
};

/**
 * Sanitize a string to remove problematic characters
 * @param {string} str - The string to sanitize
 * @returns {string} - Sanitized string
 */
const sanitizeString = (str) => {
  if (!str) return '';

  const noControlChars = Array.from(String(str))
    .filter((ch) => {
      const code = ch.charCodeAt(0);

      // Remove ASCII/C1 control chars plus problematic Unicode separators.
      if ((code >= 0x00 && code <= 0x1f) || (code >= 0x7f && code <= 0x9f)) return false;
      if (code === 0x2028 || code === 0x2029) return false;
      if (code >= 0xfff0 && code <= 0xffff) return false;

      return true;
    })
    .join('');
  
  return noControlChars
    // Remove other potentially problematic characters
    .replace(/[^\x20-\x7E\xA0-\xFF\u0100-\u017F\u0180-\u024F\u0370-\u03FF\u0400-\u04FF\s.,;:?!(){}<>\-–—]/g, ' ')
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    .trim();
};